import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Component, computed, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, DOCUMENT } from '@angular/core';
import { Subject, catchError, finalize, first, map, of, take, takeUntil, tap } from 'rxjs';
import { animate, style, transition, trigger } from '@angular/animations';

import { AuthService } from '@auth0/auth0-angular';

import { Router } from '@angular/router';
import { initFlowbite } from 'flowbite';

import { DataListCnpjs, ListCnpjsResponseModel } from '../../core/models/responses/list-cnpjs-response.model';
import { UserPermissionResponseModel } from '../../core/models/responses/user-permission-response.model';

import { PermissionService } from '../../core/services/common/permission.service';
import { AuthGuardService } from '../../core/services/common/auth-guard.service';
import { CookiesService } from '../../core/services/common/cookies.service';
import { OnboardService } from '../../core/services/common/onboard.service';
import { EventsService } from '../../core/services/common/events.service';
import { CnpjService } from '../../core/services/common/cnpj.service';
import { ApiService } from '../../core/api/api.service';

import { PermissionBaseComponent } from '../../core/abstracts/permission-base.component';

import { environment } from '../../../environments/environment';

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrl: './header.component.scss',
    animations: [
        trigger('fadeSlideInOut', [
            transition(':enter', [
                style({ opacity: 0, transform: 'translateY(10px)' }),
                animate('300ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
            ]),
            transition(':leave', [
                animate('300ms ease-out', style({ opacity: 0, transform: 'translateY(10px)' }))
            ])
        ])
    ],
    standalone: false
})
export class HeaderComponent extends PermissionBaseComponent implements OnInit, OnDestroy {
  override destroy$ = new Subject<void>();

  showFilterCNPJ: boolean = false;

  @Input() isCNPJEmpty: boolean = false;
  @Output() cnpjExists = new EventEmitter<boolean>;

  picture: string | null | undefined = '';
  productSubMenuOpen = false;
  toolsSubMenuOpen: boolean = false;
  menuOpen = false;
  announcekitWidget: string = environment.ANNOUNCEKIT_WIDGET;

  showDiv: boolean = false;
  searchValue: string = '';
  filtered: boolean = false;

  isEmailRmFarma: boolean = false;

  isMenuOpen: boolean = false;
  isMenuMobileOpen: boolean = false;

  isOpenMenuTools: boolean = false

  userPermissionResponse: UserPermissionResponseModel = {} as UserPermissionResponseModel;
  isVisibleDashboardsSale: boolean = true;
  isVisibleDashboardsBuy: boolean = true;
  isVisiblePharmaSync: boolean = false;
  isVisiblePharmaSyncWarn: boolean = false;
  isLoadingMenu: boolean = false;
  isVisibleListCnpjs: boolean = false;
  incentivePoints: string = '';

  formSearch!: FormGroup;

  listCnpjsResponse: ListCnpjsResponseModel = {} as ListCnpjsResponseModel;
  filteredCnpjs: DataListCnpjs[] = [];

  openMenu: boolean = true;

  constructor(
    @Inject(DOCUMENT) public document: Document,
    permissionService: PermissionService,
    private authGuard: AuthGuardService,
    private eventService: EventsService,
    private cnpjService: CnpjService,
    private cookies: CookiesService,
    private onboard: OnboardService,
    public auth: AuthService,
    private api: ApiService,
    private router: Router,
  ) {
    super(permissionService)
    this.buildFormSearch();
  }

  override ngOnInit(): void {
    this.checkInternoStatus();
    this.checkLicenciadoStatus();
    initFlowbite();
    this.fetchUserPicture();
    this.subscribeToEmailDomainCheck();
    this.loadCNPJFromStorage();
    this.onboard.checkDocuments();
    // setTimeout(() => {
    //   this.getUserPermission();
    // }, 500);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length) {
          this.updateElementStyle();
        }
      });
    });

    // Configuração do observer para observar o body por mudanças no DOM
    observer.observe(document.body, { childList: true, subtree: true });

    this.cnpjService.selectedCnpj$
      .pipe(takeUntil(this.destroy$))
      .subscribe((cnpj) => {
        this.searchValue = cnpj;
      });

    // setTimeout(() => {
    //   this.getCnpjs();
    // }, 2000)
  }

  private buildFormSearch(): void {
    this.formSearch = new FormGroup({
      cnpj: new FormControl('', [Validators.required, this.cnpjValidator()]),
    });
  }

  override ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  updateElementStyle(): void {
    const element = document.querySelector('div[data-announcekit-mode="badge"]');
    if (element && element instanceof HTMLElement) {
      element.style.display = 'flex';
      element.style.alignItems = 'center';
      element.style.justifyContent = 'center';
      element.style.position = 'relative';

      const badge = element.querySelector('a.announcekit-widget-badge');
      if (badge && badge instanceof HTMLElement) {
        badge.style.position = 'absolute';
        badge.style.top = '10';
        badge.style.right = '0';
      }
    }
  }

  private loadCNPJFromStorage(): void {
    let cnpj = this.cnpjService.getSelectedCnpj();
    if (cnpj) {
      this.searchValue = cnpj;
      this.cnpjService.setSelectedCnpj(this.searchValue);
      this.showDiv = true;
      this.filtered = true;
    }
  }

  private subscribeToEmailDomainCheck(): void {
    this.authGuard.isEmailDomainRmFarma().subscribe(isRmFarma => {
      this.isEmailRmFarma = isRmFarma;
    });
  }

  fetchUserPicture(): void {
    this.auth.user$.pipe(
      take(1),
      map(response => response?.picture),
      catchError(error => {
        console.error('Erro ao obter a imagem do usuário:', error);
        return of(null);
      })
    ).subscribe(picture => {
      this.picture = picture;
    });
  }

  public handleIncentivePoints(incentivePoints: string): void {
    this.incentivePoints = incentivePoints;
  }

  logout(): void {
    this.cookies.removeItem('TOKEN', '/');
    this.cookies.removeItem('TOKEN');
    this.cookies.clearCookies();
    localStorage.removeItem('CNPJ');
    localStorage.removeItem('CNPJ_NAME');
    localStorage.removeItem('savedHref');
    localStorage.removeItem('PENDENCIAS-ONBOARD');
    localStorage.removeItem('savedLocation');
    localStorage.removeItem('utm_params');
    this.eventService.rudderAnalytics?.reset();

    this.auth.logout({
      logoutParams: {
        returnTo: this.document.location.origin
      }
    });
  }

  saveCNPJ(): void {
    if (this.searchValue.length >= 14) {
      this.filtered = true
      this.filtered = true;
      this.showFilterCNPJ = false;
      this.cnpjService.setSelectedCnpj(this.searchValue);
      localStorage.setItem('CNPJ', this.searchValue);
      this.reloadPage();
      if (this.isCNPJEmpty) {
        this.cnpjExists.emit(true);
      } else {
        this.reloadPage();
      }
    }
  }

  onSearchValueChange(): void {
    if (this.searchValue.length >= 14) {
      this.showDiv = true;
    }
    if (!this.searchValue.trim()) {
      this.filtered = false;
      localStorage.removeItem('CNPJ');
      this.reloadPage();
    }
  }

  cleanFilter(): void {
    this.searchValue = '';
    this.cnpjService.setSelectedCnpj('');
    this.filtered = false;
    localStorage.removeItem('CNPJ');
    this.cnpjExists.emit(false);
    this.reloadPage();
  }

  private reloadPage(): void {
    let currentUrl = this.router.url;

    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigate([currentUrl]);
    });
  }

  toggleMenuMobile(): void {
    this.isMenuMobileOpen = !this.isMenuMobileOpen;
  }

  toggleOpenMenu(): void {
    this.openMenu = !this.openMenu;
  }

  handleCloseMenu(value: boolean) {
    this.openMenu = value;
  }

  toggleMenu(): void {
    this.isMenuOpen = !this.isMenuOpen;
    this.isOpenMenuTools = false;
  }

  toggleMenuTools(): void {
    this.isOpenMenuTools = !this.isOpenMenuTools;
    this.isMenuOpen = false;
  }

  toggleProductSubMenu(): void {
    this.productSubMenuOpen = !this.productSubMenuOpen;
  }

  toggleToolsSubMenu(): void {
    this.toolsSubMenuOpen = !this.toolsSubMenuOpen;
  }

  closeSubMenu(): void {
    this.productSubMenuOpen = false;
  }

  toggleFilterCNPJ(): void {
    // this.showFilterCNPJ = !this.showFilterCNPJ;
    this.cnpjService.showCnpjPopup(true);
    if (this.isCNPJEmpty) {
      this.showFilterCNPJ = false;
      this.isCNPJEmpty = false;
    }
  }

  private getCnpjs(): void {
    this.cnpjService.getCnpjs()
      .pipe(
        first(response => response && response.data && response.data.length > 0),
        tap((response) => {
          this.listCnpjsResponse = response;
          this.filteredCnpjs = this.listCnpjsResponse.data;
        }),
        catchError((error) => {
          console.error('Erro ao buscar lista de CNPJs', error);
          return of([]);
        })
      )
      .subscribe();
  }

  selectCnpj(cnpj: string): void {
    const hasMatchingCNPJ = this.listCnpjsResponse.data.some(item =>
      item.cnpj.toLowerCase().includes(cnpj.toLowerCase())
    );

    if (hasMatchingCNPJ) {
      this.formSearch.patchValue({
        cnpj: cnpj,
      });

      this.searchValue = cnpj;

      this.isVisibleListCnpjs = false;
    }
  }

  filterCnpjs(): void {
    if (this.listCnpjsResponse && this.listCnpjsResponse.data) {
      if (this.cnpj) {
        this.filteredCnpjs = this.listCnpjsResponse.data.filter(cnpj =>
          cnpj.cnpj.includes(this.cnpj) ||
          cnpj.nome_licenciado.toLowerCase().includes(this.cnpj.toLowerCase())
        );
      } else {
        this.filteredCnpjs = this.listCnpjsResponse.data;
      }
    } else {
      console.error('listCnpjsResponse ou listCnpjsResponse.data não estão definidos');
      this.filteredCnpjs = [];
    }
  }

  private cnpjValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      let isInvalid: boolean = false;

      if (Object.keys(this.filteredCnpjs)) {
        isInvalid = this.filteredCnpjs.some(item =>
          item.cnpj?.trim().toLowerCase() == control?.value?.trim().toLowerCase());
      }

      return isInvalid ? { invalidCNPJ: true } : null;
    };
  }

  get cnpj(): string {
    return this.formSearch.get('cnpj')?.value;
  }

  public onboardCheck(): void {
    this.onboard.checkDocuments()
    localStorage.setItem('missingDocuments', JSON.stringify(this.onboard.missingDocuments))
  }

}
